import { Fragment, useState } from 'react'
import { api } from '@/api/client'
import { useFetch, useMutate } from '@/hooks/useApi'
import { AsyncState, Button, EmptyState, PageHead, Select } from '@/components/ui'
import { dateTime } from '@/lib/format'

// Раздел доступен только super_admin (роут под <RequireAuth roles={['super_admin']}>).
// Заявки — уровень площадки (без клуба), приходят с формы лендинга 8ballpos.by.

type LeadStatus = 'new' | 'in_progress' | 'done' | 'spam'

interface Lead {
  id: string
  name: string
  club: string | null
  phone: string
  tariff: string | null
  comment: string | null
  source: string
  status: LeadStatus
  createdAt: number
  updatedAt: number
}

const STATUSES: LeadStatus[] = ['new', 'in_progress', 'done', 'spam']
const STATUS_LABEL: Record<LeadStatus, string> = {
  new: 'Новая',
  in_progress: 'В работе',
  done: 'Обработана',
  spam: 'Спам'
}
/** Цвет индикатора статуса (совпадает с палитрой .dot). */
const STATUS_DOT: Record<LeadStatus, string> = {
  new: '#3b82f6',
  in_progress: '#c9a84c',
  done: '#22c55e',
  spam: '#8a94a8'
}

const TARIFF_LABEL: Record<string, string> = {
  start: 'Старт',
  club: 'Клуб',
  restaurant: 'Ресторан',
  full: 'Full'
}

export function Leads(): React.JSX.Element {
  return (
    <>
      <PageHead
        eyebrow="Супер-админ"
        title="Заявки"
        lead="Заявки с продающего лендинга 8ballpos.by: заявки на подключение и обратную связь. Обрабатывайте воронку — меняйте статус по мере работы с клиентом."
      />
      <LeadsManager />
    </>
  )
}

function LeadsManager(): React.JSX.Element {
  const [filter, setFilter] = useState<LeadStatus | ''>('')
  const list = useFetch<Lead[]>('/v1/leads', filter ? { status: filter } : undefined, [filter])
  const [expanded, setExpanded] = useState<string | null>(null)
  const [savingId, setSavingId] = useState<string | null>(null)
  const save = useMutate()

  const rows = list.data ?? []

  const changeStatus = async (lead: Lead, status: LeadStatus): Promise<void> => {
    if (status === lead.status) return
    setSavingId(lead.id)
    const ok = await save.mutate(() => api.patch(`/v1/leads/${lead.id}`, { status }))
    setSavingId(null)
    if (ok) list.reload()
  }

  return (
    <>
      <div className="toolbar">
        <div className="toolbar__left">
          <span className="muted">{rows.length} заявок(и)</span>
        </div>
        <div className="toolbar__right" style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <Select
            style={{ width: 'auto' }}
            value={filter}
            onChange={(e) => setFilter(e.target.value as LeadStatus | '')}
            aria-label="Фильтр по статусу"
          >
            <option value="">Все статусы</option>
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {STATUS_LABEL[s]}
              </option>
            ))}
          </Select>
          <Button variant="ghost" size="sm" onClick={() => list.reload()} type="button">
            Обновить
          </Button>
        </div>
      </div>

      <AsyncState query={list} />
      {!list.loading && !list.error && rows.length === 0 && (
        <EmptyState message={filter ? 'Заявок с таким статусом нет.' : 'Заявок ещё нет.'} />
      )}

      {save.error && (
        <div className="form-error" style={{ marginBottom: 12 }}>
          {save.error}
        </div>
      )}

      {rows.length > 0 && (
        <div className="table-wrap">
          <table className="rtable">
            <thead>
              <tr>
                <th style={{ width: 150 }}>Дата</th>
                <th>Имя</th>
                <th>Телефон</th>
                <th>Клуб</th>
                <th>Тариф</th>
                <th style={{ width: 170 }}>Статус</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((l) => (
                <Fragment key={l.id}>
                  <tr
                    className={l.id === expanded ? 'is-selected' : undefined}
                    style={{ cursor: l.comment ? 'pointer' : undefined }}
                    onClick={() => l.comment && setExpanded((id) => (id === l.id ? null : l.id))}
                  >
                    <td>{dateTime(l.createdAt)}</td>
                    <td>{l.name}</td>
                    <td>
                      <a
                        href={`tel:${l.phone}`}
                        onClick={(e) => e.stopPropagation()}
                        style={{ color: 'inherit' }}
                      >
                        {l.phone}
                      </a>
                    </td>
                    <td>{l.club || '—'}</td>
                    <td>{l.tariff ? (TARIFF_LABEL[l.tariff] ?? l.tariff) : '—'}</td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <span
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
                        title={STATUS_LABEL[l.status]}
                      >
                        <span
                          style={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            background: STATUS_DOT[l.status],
                            flexShrink: 0
                          }}
                        />
                        <Select
                          style={{ width: 'auto', padding: '4px 8px', fontSize: 13 }}
                          value={l.status}
                          disabled={savingId === l.id}
                          onChange={(e) => void changeStatus(l, e.target.value as LeadStatus)}
                        >
                          {STATUSES.map((s) => (
                            <option key={s} value={s}>
                              {STATUS_LABEL[s]}
                            </option>
                          ))}
                        </Select>
                      </span>
                    </td>
                  </tr>
                  {l.id === expanded && l.comment && (
                    <tr>
                      <td colSpan={6} style={{ background: 'rgba(255,255,255,.02)' }}>
                        <span className="muted">Комментарий: </span>
                        {l.comment}
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  )
}
